import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { STATUS_GRAVACAO, STATUS_TAREFA } from "@/lib/helpers";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const CALENDARIOS_DO_TIME = [
  { id: "atendimento@netzerstudio.com.br", nome: "Atendimento" },
  { id: "felipe.rosa@netzerstudio.com.br", nome: "Felipe" },
  { id: "arthurm.leal@gmail.com", nome: "Arthur" },
];
const CATEGORIAS = {
  planejamento: { nome: "Estratégia", cor: "#3B82F6" },
  reuniao: { nome: "Reunião", cor: "#8B5CF6" },
  captacao: { nome: "Captação", cor: "#EC4899" },
  pos_producao: { nome: "Edição", cor: "#A855F7" },
  aprovacao: { nome: "Aprovação", cor: "#10B981" },
  programacao: { nome: "Programação", cor: "#06B6D4" },
  publicacao: { nome: "Entrega", cor: "#14B8A6" },
};

const chaveData = (date) => {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const dia = String(date.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const inicioDoDia = (value) => {
  const [ano, mes, dia] = (value || "").split("-").map(Number);
  if (!ano || !mes || !dia) return null;
  return new Date(ano, mes - 1, dia);
};

const normalizar = (value) => (value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const categoriaDoItem = (item) => {
  if (item.etapa && CATEGORIAS[item.etapa]) return item.etapa;
  if (["gravacao_estudio", "captacao_externa"].includes(item.tipo)) return "captacao";
  if (item.tipo === "edicao") return "pos_producao";
  if (item.tipo === "postagem") return "programacao";
  if (item.tipo === "entrega") return "publicacao";
  if (item.tipo === "roteiro") return "planejamento";
  const texto = normalizar(`${item.titulo || item.summary || ""} ${item.descricao || ""}`);
  if (/gravacao|captacao|filmagem|ensaio|fotografia/.test(texto)) return "captacao";
  if (/edicao|editar|finalizacao|tratamento|pos.producao/.test(texto)) return "pos_producao";
  if (/aprovacao|aprovar|ajuste|revisao/.test(texto)) return "aprovacao";
  if (/programa|mlabs|postagem|calendario editorial/.test(texto)) return "programacao";
  if (/entrega|publicacao|publicar|envio de material/.test(texto)) return "publicacao";
  if (/reuniao|alinhamento|onboarding|entrada/.test(texto)) return "reuniao";
  return "planejamento";
};

const clienteDoTitulo = (titulo, clientes) => {
  const tituloNormalizado = normalizar(titulo);
  const tituloTokens = tituloNormalizado.split(/[^a-z0-9]+/).filter(Boolean);
  const clientesOrdenados = [...clientes]
    .filter((item) => item.empresa)
    .sort((a, b) => b.empresa.length - a.empresa.length);
  return clientesOrdenados.find((item) => tituloNormalizado.includes(normalizar(item.empresa)))
    || clientesOrdenados.find((item) => {
      const primeiroToken = normalizar(item.empresa).split(/[^a-z0-9]+/).find((token) => token.length >= 4);
      return primeiroToken && tituloTokens.some((token) => token.startsWith(primeiroToken) || primeiroToken.startsWith(token));
    });
};

const converterEventoGoogle = (evento, calendario, clientes) => {
  const inicio = evento.start?.dateTime || evento.start?.date || "";
  const fim = evento.end?.dateTime || "";
  const cliente = clienteDoTitulo(evento.summary, clientes);
  const base = {
    id: `google:${evento.id}`,
    google_event_id: evento.id,
    evento_id_local: evento.extendedProperties?.private?.evento_id || "",
    tarefa_id: evento.extendedProperties?.private?.tarefa_id || "",
    titulo: evento.summary || "Compromisso",
    data: inicio.slice(0, 10),
    hora_inicio: inicio.includes("T") ? inicio.slice(11, 16) : "",
    hora_fim: fim.includes("T") ? fim.slice(11, 16) : "",
    cliente_id: cliente?.id || "",
    cliente_nome: cliente?.empresa || "Studio Netzer",
    responsavel: calendario.nome,
    local: evento.location || "",
    status: "agendada",
    origem_google: true,
    origem_nome: calendario.nome,
    html_link: evento.htmlLink || "",
  };
  return { ...base, categoria: categoriaDoItem(base) };
};

const criarGradeDoMes = (mes) => {
  const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const inicio = new Date(primeiro);
  inicio.setDate(primeiro.getDate() - primeiro.getDay());
  return Array.from({ length: 42 }, (_, indice) => {
    const dia = new Date(inicio);
    dia.setDate(inicio.getDate() + indice);
    return dia;
  });
};

const statusDoItem = (item) => {
  if (item.origem_tarefa) return STATUS_TAREFA[item.status] || STATUS_TAREFA.nao_iniciado;
  if (item.categoria === "captacao") return STATUS_GRAVACAO[item.status] || STATUS_GRAVACAO.agendada;
  if (item.status === "concluido") return { nome: "Concluído", cor: "#4ADE80" };
  return { nome: item.status === "em_andamento" ? "Em andamento" : "Agendado", cor: "#A1A1AA" };
};

const chaveSemantica = (item) => [
  item.data,
  item.hora_inicio || "",
  normalizar(item.titulo).replace(/\s+/g, " ").trim(),
].join("|");

export function GravacoesCalendar({ eventos, tarefas = [], clientes, onEdit, onOpenCycle }) {
  const hoje = useMemo(() => new Date(), []);
  const [mesVisivel, setMesVisivel] = useState(() => new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const [eventosGoogle, setEventosGoogle] = useState([]);
  const [carregandoGoogle, setCarregandoGoogle] = useState(false);
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    let ativo = true;
    const inicio = chaveData(new Date(mesVisivel.getFullYear(), mesVisivel.getMonth(), 1));
    const fim = chaveData(new Date(mesVisivel.getFullYear(), mesVisivel.getMonth() + 1, 1));
    setCarregandoGoogle(true);
    Promise.allSettled(CALENDARIOS_DO_TIME.map(async (calendario) => {
      const { data } = await api.get("/integracoes/google/eventos", { params: { inicio, fim, calendario: calendario.id } });
      return (data.items || []).map((evento) => converterEventoGoogle(evento, calendario, clientes));
    }))
      .then((resultados) => {
        if (!ativo) return;
        setEventosGoogle(resultados.flatMap((resultado) => resultado.status === "fulfilled" ? resultado.value : []));
      })
      .finally(() => { if (ativo) setCarregandoGoogle(false); });
    return () => { ativo = false; };
  }, [clientes, mesVisivel]);

  const itensLocais = useMemo(() => eventos
    .filter((evento) => inicioDoDia(evento.data) && !["cancelada", "cancelado"].includes(evento.status))
    .map((evento) => ({
      ...evento,
      categoria: categoriaDoItem(evento),
      cliente_nome: clientes.find((cliente) => cliente.id === evento.cliente_id)?.empresa || "Studio Netzer",
      origem_nome: "Sistema",
    })), [clientes, eventos]);

  const itensDeTarefa = useMemo(() => {
    const tarefasComEvento = new Set(itensLocais.map((evento) => evento.tarefa_id).filter(Boolean));
    return tarefas
      .filter((tarefa) => tarefa.prazo && tarefa.status !== "cancelado" && !tarefasComEvento.has(tarefa.id))
      .map((tarefa) => ({
        id: `tarefa:${tarefa.id}`,
        tarefa_id: tarefa.id,
        titulo: tarefa.titulo,
        descricao: tarefa.descricao || "",
        data: tarefa.prazo,
        hora_inicio: tarefa.agenda_hora_inicio || "",
        hora_fim: tarefa.agenda_hora_fim || "",
        cliente_id: tarefa.cliente_id || "",
        cliente_nome: clientes.find((cliente) => cliente.id === tarefa.cliente_id)?.empresa || "Studio Netzer",
        ciclo_id: tarefa.ciclo_id || "",
        responsavel: tarefa.responsavel || "Sem responsável",
        status: tarefa.status || "nao_iniciado",
        etapa: tarefa.etapa || "planejamento",
        categoria: categoriaDoItem(tarefa),
        origem_tarefa: true,
        origem_nome: "Tarefa",
      }));
  }, [clientes, itensLocais, tarefas]);

  const agendaCompleta = useMemo(() => {
    const idsLocais = new Set(itensLocais.map((item) => item.id));
    const tarefasLocais = new Set(itensLocais.map((item) => item.tarefa_id).filter(Boolean));
    const googleLocais = new Set(itensLocais.map((item) => item.google_event_id).filter(Boolean));
    const chaves = new Set([...itensLocais, ...itensDeTarefa].map(chaveSemantica));
    const idsGoogle = new Set();
    const externos = eventosGoogle.filter((item) => {
      if (idsGoogle.has(item.google_event_id)) return false;
      idsGoogle.add(item.google_event_id);
      const duplicado = googleLocais.has(item.google_event_id)
        || idsLocais.has(item.evento_id_local)
        || tarefasLocais.has(item.tarefa_id)
        || chaves.has(chaveSemantica(item));
      if (!duplicado) chaves.add(chaveSemantica(item));
      return !duplicado;
    });
    return [...itensLocais, ...itensDeTarefa, ...externos]
      .sort((a, b) => `${a.data} ${a.hora_inicio || "00:00"}`.localeCompare(`${b.data} ${b.hora_inicio || "00:00"}`));
  }, [eventosGoogle, itensDeTarefa, itensLocais]);

  const itensFiltrados = useMemo(() => agendaCompleta.filter((item) => filtro === "todos" || item.categoria === filtro), [agendaCompleta, filtro]);
  const eventosPorDia = useMemo(() => itensFiltrados.reduce((grupo, item) => {
    if (!grupo[item.data]) grupo[item.data] = [];
    grupo[item.data].push(item);
    return grupo;
  }, {}), [itensFiltrados]);

  const dias = useMemo(() => criarGradeDoMes(mesVisivel), [mesVisivel]);
  const tituloMes = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(mesVisivel);
  const totalMes = itensFiltrados.filter((item) => {
    const data = inicioDoDia(item.data);
    return data && data.getFullYear() === mesVisivel.getFullYear() && data.getMonth() === mesVisivel.getMonth();
  }).length;
  const mudarMes = (direcao) => setMesVisivel((atual) => new Date(atual.getFullYear(), atual.getMonth() + direcao, 1));
  const voltarHoje = () => setMesVisivel(new Date(hoje.getFullYear(), hoje.getMonth(), 1));

  const abrirItem = (item) => {
    if (item.ciclo_id && onOpenCycle) return onOpenCycle(item.ciclo_id);
    if (!item.origem_google && item.categoria === "captacao" && onEdit) return onEdit(item);
    if (item.origem_google && item.html_link) window.open(item.html_link, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="studio-card overflow-hidden" data-testid="gravacoes-calendar">
      <div className="border-b border-[#27272A] p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="caption-label">Agenda da produção</p>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <h2 className="font-serif text-2xl capitalize text-[#F4F4F0]">{tituloMes}</h2>
              <span className="font-mono text-[11px] text-[#71717A]">{carregandoGoogle ? "Atualizando o time…" : `${totalMes} atividade(s)`}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={voltarHoje} className="rounded-full border-[#27272A] text-[#A1A1AA] hover:text-[#F4F4F0]">Hoje</Button>
            <button type="button" onClick={() => mudarMes(-1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] text-[#A1A1AA] transition-colors hover:border-[#8CC63F]/50 hover:text-[#8CC63F]" aria-label="Mês anterior"><ChevronLeft size={17} /></button>
            <button type="button" onClick={() => mudarMes(1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] text-[#A1A1AA] transition-colors hover:border-[#8CC63F]/50 hover:text-[#8CC63F]" aria-label="Próximo mês"><ChevronRight size={17} /></button>
          </div>
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" aria-label="Filtros da agenda">
          <button type="button" onClick={() => setFiltro("todos")} className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider ${filtro === "todos" ? "border-[#8CC63F]/50 bg-[#8CC63F]/10 text-[#8CC63F]" : "border-[#27272A] text-[#71717A]"}`}>Todos</button>
          {Object.entries(CATEGORIAS).map(([key, item]) => (
            <button key={key} type="button" onClick={() => setFiltro(key)} className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider ${filtro === key ? "border-[#8CC63F]/50 bg-[#8CC63F]/10 text-[#F4F4F0]" : "border-[#27272A] text-[#71717A]"}`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: item.cor }} />{item.nome}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[860px]">
          <div className="grid grid-cols-7 border-b border-[#27272A] bg-[#101014]">
            {DIAS_SEMANA.map((dia) => <div key={dia} className="px-2 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-[#71717A]">{dia}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {dias.map((dia) => {
              const chave = chaveData(dia);
              const foraDoMes = dia.getMonth() !== mesVisivel.getMonth();
              const ehHoje = chave === chaveData(hoje);
              const eventosDoDia = eventosPorDia[chave] || [];
              return (
                <div key={chave} className={`min-h-36 border-b border-r border-[#27272A] p-2 ${foraDoMes ? "bg-[#0B0B0E]/70" : "bg-[#141418]"}`}>
                  <div className={`mb-2 flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs ${ehHoje ? "bg-[#8CC63F] font-semibold text-[#09090B]" : foraDoMes ? "text-[#3F3F46]" : "text-[#A1A1AA]"}`}>{dia.getDate()}</div>
                  <div className="space-y-1.5">
                    {eventosDoDia.map((item) => {
                      const categoria = CATEGORIAS[item.categoria] || CATEGORIAS.planejamento;
                      const status = statusDoItem(item);
                      const clicavel = Boolean(item.ciclo_id || (!item.origem_google && item.categoria === "captacao") || (item.origem_google && item.html_link));
                      return (
                        <button key={item.id} type="button" onClick={() => abrirItem(item)} disabled={!clicavel} data-testid={`gravacao-calendar-event-${item.id}`} className={`group w-full rounded-lg border border-[#27272A] bg-[#18181C] px-2 py-1.5 text-left transition-colors ${clicavel ? "hover:border-[#8CC63F]/50 hover:bg-[#1D1D21]" : "cursor-default"}`} title={`${categoria.nome} · ${item.responsavel || item.origem_nome || "Studio Netzer"}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: categoria.cor }} />
                            <span className="truncate font-mono text-[9px] font-semibold text-[#E4E4E0]">{item.hora_inicio || "Prazo"} · {item.cliente_nome}</span>
                            {item.origem_google
                              ? <span className="ml-auto shrink-0 rounded-full border border-[#3B82F6]/30 px-1.5 py-0.5 font-mono text-[7px] uppercase tracking-wider text-[#60A5FA]">{item.origem_nome}</span>
                              : item.categoria === "captacao" && <Pencil size={10} className="ml-auto shrink-0 text-[#52525B] group-hover:text-[#8CC63F]" />}
                          </div>
                          <p className="mt-1 line-clamp-2 text-[10px] leading-tight text-[#A1A1AA]">{item.titulo}</p>
                          <div className="mt-1 flex items-center justify-between gap-1 font-mono text-[8px]">
                            <span className="truncate" style={{ color: status.cor }}>{status.nome}</span>
                            <span className="truncate text-[#52525B]">{item.responsavel || item.origem_nome}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
